import { useEffect, useRef, useState } from "react";
import { Volume2 } from "lucide-react";
import VideoPlaceholder from "@/components/7fcc/VideoPlaceholder";
import { scrollToId } from "@/lib/scroll";

// HTML5 VSL player: responsive 16:9, autostarts MUTED on page load (browser
// policy blocks unmuted autoplay) with a "Tap For Sound" pill while muted,
// native playback controls, playsinline on mobile. The video fits inside
// the frame (letterboxed, never stretched/cropped) inside the page frame.
// When the video finishes, the page scrolls to the qualification form.
export default function VslPlayer({ src, label, testId }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  // Autostart: enforce muted before playback and kick off play() — the
  // autoplay attribute alone can be ignored under strict browser policies.
  // Retry once the first frame is decodable in case the first play() raced
  // the network load.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const kick = () => {
      v.muted = true;
      v.play().catch(() => {});
    };
    kick();
    v.addEventListener("loadeddata", kick, { once: true });
    return () => v.removeEventListener("loadeddata", kick);
  }, []);

  // No source configured → keep the placeholder frame.
  if (!src) return <VideoPlaceholder label={label} testId={testId} />;

  const unmute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    if (v.paused) v.play().catch(() => {});
  };

  return (
    <div
      className="relative aspect-video w-full overflow-hidden rounded-lg border border-line bg-ink-950 shadow-panel"
      data-testid={testId}
    >
      <video
        ref={videoRef}
        src={src}
        controls
        autoPlay
        muted
        playsInline
        preload="metadata"
        disablePictureInPicture
        controlsList="nodownload"
        onContextMenu={(e) => e.preventDefault()}
        onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
        onEnded={() => scrollToId("qualify")}
        className="absolute inset-0 h-full w-full object-contain"
      />
      {muted && (
        <button
          type="button"
          onClick={unmute}
          aria-label="Turn the sound on"
          className="absolute right-3 top-3 z-10 flex items-center gap-2 rounded-full bg-ink-950/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-fg backdrop-blur-sm transition-colors duration-300 hover:bg-ink-900"
          data-testid={testId ? `${testId}-unmute` : "vsl-unmute"}
        >
          <Volume2 size={14} aria-hidden="true" />
          Tap For Sound
        </button>
      )}
    </div>
  );
}
